import React, { useEffect, memo, useState, useRef } from 'react';
import './index.less';
import { Pagination, Row, Col, Skeleton, Image, Typography } from 'antd';
import { IPredictionScoreExamPointRecommendResource } from '@/types';
import { getPredictScoreExamPointRecommendResource } from '@/apis';
import { IInitInfo } from '../..';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

const { Paragraph } = Typography;

dayjs.extend(duration);

// 将秒数转换为时:分:秒
const formatSeconds = (seconds: number) => {
    const duration = dayjs.duration(seconds, 'seconds');
    const hours = String(duration.hours()).padStart(2, '0');
    const minutes = String(duration.minutes()).padStart(2, '0');
    const secs = String(duration.seconds()).padStart(2, '0');
    return `${hours}:${minutes}:${secs}`;
};

type IList = IPredictionScoreExamPointRecommendResource['list'];

const IMAGE_COVERS: { [key: string]: string } = {
    live_new: 'https://simg01.gaodunwangxiao.com/uploadfiles/tmp/upload/202503/05/1c61e_20250305102956.png',
    video: 'https://simg01.gaodunwangxiao.com/uploadfiles/tmp/upload/202503/05/eb1ef_20250305103013.png',
};

type Either<T1, T2> = (T1 & { [K in keyof T2]?: never }) | (T2 & { [K in keyof T1]?: never });

// 示例用法
interface CourseId {
    courseId: number;
}

interface KnowledgeId {
    knowledgeId: number;
}

type CourseIdOrKnowledgeId = Either<CourseId, KnowledgeId>;

interface IProps {
    initInfo: IInitInfo;
    id: CourseIdOrKnowledgeId;
    defaultList: IList;
    defaultPagination:
        | {
              currentPage: number;
              totalCount: number;
              pageSize: number;
          }
        | undefined;
    handleOpenVideoModal: (videoInfo: IList[number]) => void;
}

const VideoList = (props: IProps) => {
    const {
        initInfo,
        id,
        defaultList = [],
        defaultPagination = {
            currentPage: 1,
            totalCount: 0,
            pageSize: 8,
        },
        handleOpenVideoModal,
    } = props;

    const [list, setList] = useState<IList>(defaultList);
    const [pagination, setPagination] = useState<{ currentPage: number; totalCount: number; pageSize: number }>({
        ...defaultPagination,
    });
    const [loading, setLoading] = useState(false);
    const skeletons = useRef(Array.from({ length: 8 }));

    const fetchList = async (params: { pageNum: number; pageSize: number }) => {
        setLoading(true);
        const { knowledgeId, projectId, subjectId, examSeasonId } = initInfo!;
        const response = await getPredictScoreExamPointRecommendResource({
            // courseId: courseId,
            ...id,
            examSeasonId,
            knowledgeId,
            projectId,
            subjectId,
            queryType: id.courseId ? 1 : 2, // 查询类型 0 首次进入，1 查询指定课程(courseId不能为空) 2 查询考点
            ...params,
        });
        if (response.status === 0 && response.result) {
            setList(response.result.list);
            setPagination({
                currentPage: response.result.currentPage,
                totalCount: response.result.totalCount,
                pageSize: response.result.pageSize,
            });
        }
        setLoading(false);
    };

    const onChangePagination = (page: number, pageSize: number) => {
        fetchList({
            pageNum: page,
            pageSize: pageSize,
        });
    };

    useEffect(() => {
        if (!defaultList?.length) {
            fetchList({
                pageNum: pagination.currentPage,
                pageSize: pagination.pageSize,
            });
        }
    }, []);

    return (
        <>
            <div className="sub-tiku__video-list">
                <Row gutter={[20, 16]}>
                    {!loading &&
                        list?.length &&
                        list.map(item => (
                            <Col flex="160px" key={`${item.csItemId}_${item.resourceId}`}>
                                <div className="sub-tiku__video-item" onClick={() => handleOpenVideoModal(item)}>
                                    <div className="sub-tiku__video-cover">
                                        <Image
                                            width={160}
                                            height={90}
                                            preview={false}
                                            src={item.coverPic || IMAGE_COVERS[item.discriminator]}
                                            fallback={IMAGE_COVERS[item.discriminator]}
                                        />
                                        <span className="sub-tiku__video-time">{formatSeconds(item.duration)}</span>
                                    </div>
                                    <Paragraph className="sub-tiku__video-title" ellipsis={{ rows: 2, expandable: false }}>
                                        {item.resourceName}
                                    </Paragraph>
                                </div>
                            </Col>
                        ))}
                    {loading &&
                        skeletons.current.map((_, index) => (
                            <Col flex="160px" key={`${index + 1}`}>
                                <Skeleton.Node active style={{ width: 160, height: 110 }} />
                            </Col>
                        ))}
                </Row>
            </div>
            <Pagination
                size="small"
                align="center"
                pageSize={pagination.pageSize}
                hideOnSinglePage
                current={pagination.currentPage}
                total={pagination.totalCount}
                onChange={onChangePagination}
            />
        </>
    );
};

export default memo(VideoList);
